import { ArrowUpRight, ChevronDown, CornerUpLeft } from "lucide-react";
import React from "react";
import { rewriteRelativeUrls, withAvatarBase } from "../../gitlabHtml";
import { workingDaysSince } from "../../hooks/commentAttention";
import { safeHttpUrl } from "../../utils";
import { Avatar } from "../Avatar";
import { CodeSnippet } from "../CodeSnippet";
import { DateTime } from "../DateTime";

// Unanswered for this many working days or more reads as overdue.
export const OVERDUE_WORKING_DAYS = 2;

// One-line plain-text preview of a comment for collapsed rows.
export const previewOf = (body) => (body || "").replace(/\s+/g, " ").trim();

export const CollapseToggle = ({
	collapsed,
	onToggle,
	label,
	controls,
	className,
}) => (
	<button
		type="button"
		onClick={onToggle}
		aria-expanded={!collapsed}
		aria-controls={controls}
		aria-label={`${collapsed ? "Expand" : "Collapse"} ${label}`}
		title={collapsed ? "Expand (x)" : "Collapse (x)"}
		data-nav-toggle
		className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[var(--response-subtle-text)] hover:bg-[var(--response-panel)] hover:text-[var(--color-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] active:scale-[0.96] transition-[background-color,color,transform] ${className || ""}`}
	>
		<ChevronDown
			className={`h-4 w-4 transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`}
		/>
	</button>
);

// Relative time, amber once the thread has waited OVERDUE_WORKING_DAYS.
const ThreadAge = ({ createdAt }) => {
	const days = workingDaysSince(createdAt);
	const overdue = days >= OVERDUE_WORKING_DAYS;
	return (
		<span
			className={
				overdue ? "font-medium text-[var(--status-overdue-text)]" : undefined
			}
		>
			<DateTime date={createdAt} />
			{overdue && (
				<span className="sr-only">
					, waiting {days} working {days === 1 ? "day" : "days"}
				</span>
			)}
		</span>
	);
};

export const ResponseThread = ({
	thread,
	position,
	project,
	collapsed,
	onToggle,
	compact = false,
}) => {
	const commentUrl = safeHttpUrl(thread.url);
	const authorName = thread.author.name || thread.author.username;
	const bodyId = `thread-body-${thread.id}`;

	return (
		<article
			data-nav-item
			tabIndex={-1}
			aria-label={`Thread from ${authorName}`}
			className={`grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-x-3 rounded-2xl first:pt-0 last:pb-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--response-panel)] ${
				compact ? "py-1" : "py-2.5"
			}`}
		>
			<Avatar
				src={withAvatarBase(thread.author.avatarUrl)}
				alt={authorName}
				size={compact ? "sm" : "md"}
				className="row-start-1 self-start mt-1 shrink-0 ring-1 ring-black/10 dark:ring-white/10"
			/>
			<div
				className={`response-comment relative col-start-2 row-span-full min-w-0 rounded-2xl rounded-tl-md bg-[var(--response-bubble)] shadow-[0_1px_2px_rgb(0_0_0/0.06)] ring-1 ring-[var(--response-ring)] ${
					compact ? "px-3" : "px-4"
				} ${collapsed ? "py-1" : compact ? "py-2" : "py-3"}`}
			>
				<div className="flex min-h-[2rem] items-center justify-between gap-x-3">
					<div className="flex min-w-0 flex-1 items-center gap-2">
						<CollapseToggle
							collapsed={collapsed}
							onToggle={onToggle}
							label={`thread from ${authorName}`}
							controls={bodyId}
							className="-ml-2"
						/>
						<span className="shrink-0 truncate text-sm font-semibold text-[var(--color-text-primary)]">
							{authorName}
						</span>
						{position > 1 && (
							<span className="shrink-0 text-xs text-[var(--response-subtle-text)]">
								Thread {position}
							</span>
						)}
						{collapsed && thread.code && (
							<span className="shrink-0 text-xs tabular-nums text-[var(--response-muted-text)]">
								{thread.code.path.split("/").pop()}:{thread.code.line}
							</span>
						)}
						{collapsed && (
							<span className="min-w-0 truncate text-sm text-[var(--response-muted-text)]">
								{previewOf(thread.body)}
							</span>
						)}
					</div>
					<div className="flex shrink-0 items-center gap-1 text-xs tabular-nums text-[var(--response-subtle-text)]">
						<ThreadAge createdAt={thread.createdAt} />
						{commentUrl && (
							<a
								href={commentUrl}
								target="_blank"
								rel="noopener noreferrer"
								data-nav-open
								className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[var(--response-subtle-text)] hover:bg-[var(--response-panel)] hover:text-[var(--color-link)] active:scale-[0.96] transition-transform"
								aria-label="Open comment in GitLab"
								title="Open in GitLab (o)"
							>
								<ArrowUpRight className="h-4 w-4" />
							</a>
						)}
					</div>
				</div>
				{!collapsed && (
					<div id={bodyId}>
						<CodeSnippet
							project={project}
							code={thread.code}
							compact={compact}
						/>
						{thread.yourLast && (
							<p
								className="mt-2 flex min-w-0 items-center gap-1.5 text-xs text-[var(--response-subtle-text)]"
								title={thread.yourLast.body}
							>
								<CornerUpLeft className="h-3 w-3 shrink-0" />
								<span className="shrink-0">
									You, <DateTime date={thread.yourLast.createdAt} />:
								</span>
								<span className="min-w-0 truncate">
									{previewOf(thread.yourLast.body)}
								</span>
							</p>
						)}
						{thread.bodyHtml ? (
							<div
								className="rb-comment-html mt-1.5 min-w-0 text-sm leading-relaxed text-[var(--color-text-primary)]"
								// biome-ignore lint/security/noDangerouslySetInnerHtml: GitLab sanitizes bodyHtml before returning it.
								dangerouslySetInnerHTML={{
									__html: rewriteRelativeUrls(thread.bodyHtml),
								}}
							/>
						) : (
							<p className="mt-1.5 whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm leading-relaxed text-[var(--color-text-primary)] [text-wrap:pretty]">
								{thread.body}
							</p>
						)}
					</div>
				)}
			</div>
		</article>
	);
};
